import type { ColumnDef } from '@tanstack/react-table'
import { MoreHorizontal } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import type { RouterInputs, RouterOutputs } from 'shared/api'
import { trpcClient } from 'shared/api'
import { DataTable } from 'shared/components/data-table/data-table'
import { ToneNote } from 'shared/components/tone-banner'
import {
	AlertDialog,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from 'shared/components/ui/alert-dialog'
import { Button } from 'shared/components/ui/button'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from 'shared/components/ui/dropdown-menu'
import { Textarea } from 'shared/components/ui/textarea'
import { useDataTable } from 'shared/hooks/use-data-table'
import { useTranslate } from 'shared/lib/i18n'
import { formatDayTime } from 'shared/lib/utils/date'

export type AwaitingReturn = RouterOutputs['buyerReturns']['Awaiting'][number]
export type CancelReturnInput = RouterInputs['buyerReturns']['Cancel']
export type CancelReturnAnswer = RouterOutputs['buyerReturns']['Cancel']

const awaitingViaTrpc = () => trpcClient.buyerReturns.Awaiting.query()
const cancelViaTrpc = (input: CancelReturnInput) => trpcClient.buyerReturns.Cancel.mutate(input)

/**
 * «Зарегистрированы, ещё не приняты» — the returns of partners without an API not yet
 * accepted at a Point (ticket 44), as one table: code, partner, buyer, Point, when.
 * «Отменить возврат» waits in the row's «⋯» and is confirmed, red, with support's
 * comment, which goes into the return's history; the partner has it as a
 * cancellation. A refusal is said in words (accepted already, the partner cancels by
 * its API). The calls are injected so the list is driven at its props in `bun test`.
 */
export function AwaitingReturns({
	listAwaiting = awaitingViaTrpc,
	cancel = cancelViaTrpc,
	refreshKey = 0,
	onCount,
}: {
	listAwaiting?: () => Promise<AwaitingReturn[]>
	cancel?: (input: CancelReturnInput) => Promise<CancelReturnAnswer>
	/** Changes when a return was just registered, to read the list again. */
	refreshKey?: number
	/** Told how many there are, for the section's count. */
	onCount?: (count: number) => void
}) {
	const { t } = useTranslate()
	const [items, setItems] = useState<AwaitingReturn[]>([])
	const [cancelling, setCancelling] = useState<AwaitingReturn | null>(null)
	const [comment, setComment] = useState('')
	const [busy, setBusy] = useState(false)
	const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null)

	const reload = useCallback(() => {
		listAwaiting().then(
			(next) => {
				setItems(next)
				onCount?.(next.length)
			},
			() => setItems([]),
		)
	}, [listAwaiting, onCount])
	useEffect(() => reload(), [reload, refreshKey])

	const submit = async () => {
		if (!cancelling || busy) return
		const text = comment.trim()
		if (!text) {
			setMessage({ ok: false, text: t('returnCancel.commentRequired') })
			return
		}
		setBusy(true)
		setMessage(null)
		try {
			const answer = await cancel({ parcelId: cancelling.parcelId, comment: text })
			if (answer.outcome === 'refused') {
				setMessage({ ok: false, text: t(`returnCancel.refused_${answer.reason}` as never) })
				return
			}
			setMessage({ ok: true, text: t('returnCancel.done', { code: cancelling.trackingCode }) })
			setCancelling(null)
			reload()
		} catch (err) {
			setMessage({ ok: false, text: err instanceof Error ? err.message : String(err) })
		} finally {
			setBusy(false)
		}
	}

	const columns = useMemo<ColumnDef<AwaitingReturn>[]>(
		() => [
			{
				id: 'code',
				header: t('returnCancel.colCode'),
				cell: ({ row: { original: r } }) => <span className='font-mono'>{r.trackingCode}</span>,
			},
			{ id: 'partner', header: t('returnCancel.colPartner'), cell: ({ row: { original: r } }) => r.partnerName },
			{ id: 'buyer', header: t('returnCancel.colBuyer'), cell: ({ row: { original: r } }) => r.recipientName },
			{ id: 'point', header: t('returnCancel.colPoint'), cell: ({ row: { original: r } }) => r.pointCode },
			{
				id: 'at',
				header: t('returnCancel.colRegistered'),
				cell: ({ row: { original: r } }) => (
					<span className='text-muted-foreground tabular-nums'>{formatDayTime(r.registeredAt)}</span>
				),
			},
			{
				id: 'actions',
				header: '',
				meta: { cellClassName: 'w-px text-right' },
				cell: ({ row: { original: r } }) => (
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button
								variant='ghost'
								size='icon-sm'
								aria-label={t('returnCancel.actions')}
								data-testid={`awaiting-return-menu-${r.parcelId}`}
							>
								<MoreHorizontal />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align='end'>
							<DropdownMenuItem
								variant='destructive'
								onSelect={() => {
									setCancelling(r)
									setComment('')
									setMessage(null)
								}}
								data-testid='awaiting-return-cancel'
							>
								{t('returnCancel.confirm')}
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				),
			},
		],
		[t],
	)

	const { table } = useDataTable({
		data: items,
		columns,
		rowCount: items.length,
		manualPagination: false,
		manualSorting: false,
		manualFiltering: false,
		enableRowSelection: false,
		autoResetPageIndex: false,
		getRowId: (r) => r.parcelId,
		initialState: { pagination: { pageIndex: 0, pageSize: 1000 } },
	})

	return (
		<div className='flex flex-col gap-3' data-testid='awaiting-returns'>
			{message?.ok && (
				<ToneNote tone='ready' title={message.text} testId='awaiting-returns-message' />
			)}
			<DataTable
				table={table}
				withPagination={false}
				emptyText={<span data-testid='awaiting-returns-empty'>{t('returnCancel.empty')}</span>}
				row={{ attributes: (r) => ({ 'data-testid': `awaiting-return-${r.parcelId}` }) }}
			/>
			<AlertDialog open={cancelling !== null} onOpenChange={(open) => !open && !busy && setCancelling(null)}>
				<AlertDialogContent className='data-[size=default]:sm:max-w-lg' data-testid='awaiting-return-dialog'>
					<AlertDialogHeader>
						<AlertDialogTitle>{t('returnCancel.ask', { code: cancelling?.trackingCode ?? '' })}</AlertDialogTitle>
						<AlertDialogDescription>
							{cancelling && [cancelling.partnerName, cancelling.recipientName, cancelling.pointCode].join(' · ')}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<Textarea
						aria-label={t('returnCancel.comment')}
						placeholder={t('returnCancel.commentPlaceholder')}
						value={comment}
						onChange={(e) => setComment(e.target.value)}
						data-testid='awaiting-return-comment'
					/>
					{message && !message.ok && (
						<p role='alert' className='text-sm text-destructive' data-testid='awaiting-returns-message'>
							{message.text}
						</p>
					)}
					<AlertDialogFooter>
						<AlertDialogCancel disabled={busy}>{t('returnCancel.keep')}</AlertDialogCancel>
						<Button variant='destructive' disabled={busy} onClick={submit} data-testid='awaiting-return-confirm'>
							{t('returnCancel.confirm')}
						</Button>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	)
}
